/**
 * First-time setup for a new shop.
 *
 * A shop created from the owner's panel starts with `setupDone: false`. The
 * first time somebody who can change the settings signs in, this walks them
 * through what makes the system theirs: the shop's name and logo, what prints
 * at the top of a receipt, the receipt's language and closing line, the shop's
 * colour, and a password of their own in place of the one they were handed.
 *
 * Every step is saved as they go, so closing the browser halfway loses
 * nothing. "Set up later" hides it until the next sign-in; finishing it marks
 * the shop done for good. Everything here is also in Settings, for afterwards.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ACCENT_PRESETS, accentShades, DEFAULT_ACCENT, type StoreSettings } from '@pos/shared';
import { useI18n, useT } from '../../core/i18n/index.js';
import { useSettings } from '../../core/state/settings.js';
import { useAuth } from '../../core/state/auth.js';
import { useToast } from '../../core/state/toast.js';
import { api, setToken } from '../../core/api.js';
import { Button, Field } from '../../core/ui/ui.js';
import { ShopMark } from '../../core/ui/ShopMark.js';
import { LogoError, logoFromFile } from '../../core/image.js';
import { applyTheme, resolveMode } from '../../core/theme.js';
import { printPage, receiptHtml, type ReceiptData } from '../../core/print.js';
import { rememberForOffline } from '../../core/offlineLogin.js';

const SKIP_KEY = 'pos.setupLater';

// ---------------------------------------------------------------------------
// The logo picker, shared with Settings -> The shop
// ---------------------------------------------------------------------------

export function LogoPicker({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  const t = useT();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      onChange(await logoFromFile(file));
    } catch (e) {
      const reason = e instanceof LogoError ? e.reason : 'unreadable';
      setError(
        reason === 'not_image' ? t('setup.logoNotImage') : reason === 'too_big' ? t('setup.logoTooBig') : t('setup.logoUnreadable'),
      );
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <div className="logo-picker">
      <div className={`logo-box ${value ? '' : 'empty'}`}>
        {value ? <img src={value} alt="" /> : <span aria-hidden>🖼</span>}
      </div>
      <div className="col" style={{ gap: 'var(--s-2)' }}>
        <div className="btn-row">
          <Button type="button" busy={busy} onClick={() => input.current?.click()}>
            {value ? t('setup.logoChange') : t('setup.logoUpload')}
          </Button>
          {value ? (
            <Button type="button" variant="ghost" onClick={() => onChange(null)}>
              {t('setup.logoRemove')}
            </Button>
          ) : null}
        </div>
        {error ? (
          <div className="small" role="alert" style={{ color: 'var(--danger)' }}>
            {error}
          </div>
        ) : null}
      </div>
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/*"
        hidden
        onChange={(e) => void pick(e.target.files?.[0])}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// The walk-through
// ---------------------------------------------------------------------------

type Step = 'shop' | 'details' | 'receipt' | 'look' | 'password' | 'done';
const STEPS: Step[] = ['shop', 'details', 'receipt', 'look', 'password', 'done'];

/** Shown only when this shop has not been set up and this person may set it up. */
export function SetupGate() {
  const { user, can } = useAuth();
  const { rawSettings } = useSettings();
  const [later, setLater] = useState(() => {
    try {
      return sessionStorage.getItem(SKIP_KEY) === '1';
    } catch {
      return false;
    }
  });
  if (!user || !can('settings.edit') || rawSettings.setupDone !== false || later) return null;
  return (
    <SetupWizard
      onLater={() => {
        try {
          sessionStorage.setItem(SKIP_KEY, '1');
        } catch {
          /* private window: it simply shows again next time */
        }
        setLater(true);
      }}
    />
  );
}

function SetupWizard({ onLater }: { onLater: () => void }) {
  const t = useT();
  const { lang } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { rawSettings, save } = useSettings();
  const [draft, setDraft] = useState<StoreSettings>(rawSettings);
  const [step, setStep] = useState<Step>('shop');
  const [busy, setBusy] = useState(false);
  const index = STEPS.indexOf(step);

  // Colours change as they are picked; the saved look comes back if they leave.
  useEffect(() => {
    applyTheme(draft.theme, draft.accent);
  }, [draft.theme, draft.accent]);
  useEffect(
    () => () => {
      applyTheme(rawSettings.theme, rawSettings.accent);
    },
    [rawSettings.theme, rawSettings.accent],
  );

  const name =
    (lang === 'ar' ? draft.storeNameAr || draft.storeNameEn : draft.storeNameEn || draft.storeNameAr) || t('app.name');

  /** What each step owns, saved when leaving it. */
  const fields: Record<Step, (keyof StoreSettings)[]> = {
    shop: ['storeNameAr', 'storeNameEn', 'logoDataUrl'],
    details: ['address', 'phone', 'taxNumber', 'vatRate'],
    receipt: ['receiptLang', 'receiptFooterAr', 'receiptFooterEn'],
    look: ['theme', 'accent'],
    password: [],
    done: [],
  };

  const go = async (to: Step) => {
    const keys = fields[step];
    if (keys.length && STEPS.indexOf(to) > index) {
      setBusy(true);
      try {
        const patch: Partial<StoreSettings> = {};
        for (const k of keys) (patch as Record<string, unknown>)[k] = draft[k];
        await save(patch);
      } catch (e) {
        toast.err((e as Error).message);
        setBusy(false);
        return;
      }
      setBusy(false);
    }
    setStep(to);
  };

  const finish = async (path: string) => {
    setBusy(true);
    try {
      await save({ setupDone: true });
      navigate(path);
    } catch (e) {
      toast.err((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const namesOk = !!(draft.storeNameAr.trim() || draft.storeNameEn.trim());
  const vatOk = Number.isFinite(draft.vatRate) && draft.vatRate >= 0 && draft.vatRate <= 100;
  const canNext = step === 'shop' ? namesOk : step === 'details' ? vatOk : true;

  return (
    <div className="setup" role="dialog" aria-modal="true" aria-label={t('setup.title')}>
      <div className="setup-card">
        <header className="setup-head">
          <ShopMark name={name} logo={draft.logoDataUrl} size={44} />
          <div className="grow">
            <h1>{t('setup.title')}</h1>
            <p className="small muted">{t('setup.intro')}</p>
          </div>
          {step !== 'done' ? (
            <Button variant="ghost" size="sm" onClick={onLater}>
              {t('setup.later')}
            </Button>
          ) : null}
        </header>

        <ol className="setup-steps" aria-label={t('setup.title')}>
          {STEPS.map((s, i) => (
            <li key={s} className={i < index ? 'past' : i === index ? 'now' : ''} aria-current={i === index ? 'step' : undefined}>
              <span className="n">{i < index ? '✓' : i + 1}</span>
              <span className="l">{t(`setup.step_${s}` as Parameters<typeof t>[0])}</span>
            </li>
          ))}
        </ol>

        <div className="setup-body">
          {step === 'shop' ? <ShopStep draft={draft} setDraft={setDraft} name={name} /> : null}
          {step === 'details' ? <DetailsStep draft={draft} setDraft={setDraft} /> : null}
          {step === 'receipt' ? <ReceiptStep draft={draft} setDraft={setDraft} cashier={user?.name ?? ''} /> : null}
          {step === 'look' ? <LookStep draft={draft} setDraft={setDraft} /> : null}
          {step === 'password' ? <PasswordStep onDone={() => void go('done')} /> : null}
          {step === 'done' ? <DoneStep name={name} busy={busy} onFinish={(p) => void finish(p)} /> : null}
        </div>

        {step !== 'done' && step !== 'password' ? (
          <footer className="setup-foot">
            {index > 0 ? (
              <Button onClick={() => void go(STEPS[index - 1]!)} disabled={busy}>
                {t('common.back')}
              </Button>
            ) : null}
            <div className="spacer" />
            <Button variant="primary" size="lg" busy={busy} disabled={!canNext} onClick={() => void go(STEPS[index + 1]!)}>
              {t('common.next')}
            </Button>
          </footer>
        ) : null}
        {step === 'password' ? (
          <footer className="setup-foot">
            <Button onClick={() => void go('look')}>{t('common.back')}</Button>
            <div className="spacer" />
            <Button variant="ghost" onClick={() => void go('done')}>
              {t('setup.pwKeep')}
            </Button>
          </footer>
        ) : null}
      </div>
    </div>
  );
}

type StepProps = { draft: StoreSettings; setDraft: (s: StoreSettings) => void };

function ShopStep({ draft, setDraft, name }: StepProps & { name: string }) {
  const t = useT();
  return (
    <div className="setup-split">
      <div className="col">
        <h2>{t('setup.shopH')}</h2>
        <p className="muted">{t('setup.shopP')}</p>
        <Field label={`${t('settings.storeName')} (العربية)`}>
          <input value={draft.storeNameAr} onChange={(e) => setDraft({ ...draft, storeNameAr: e.target.value })} dir="rtl" autoFocus />
        </Field>
        <Field label={`${t('settings.storeName')} (English)`}>
          <input value={draft.storeNameEn} onChange={(e) => setDraft({ ...draft, storeNameEn: e.target.value })} dir="ltr" />
        </Field>
        <Field label={t('setup.logo')} hint={t('setup.logoHint')}>
          <LogoPicker value={draft.logoDataUrl ?? null} onChange={(logoDataUrl) => setDraft({ ...draft, logoDataUrl })} />
        </Field>
      </div>
      <aside className="setup-preview">
        <div className="xsmall muted">{t('setup.previewMenu')}</div>
        <div className="setup-menu-preview">
          <ShopMark name={name} logo={draft.logoDataUrl} />
          <div style={{ minWidth: 0 }}>
            <div className="nav-brand-name">{name}</div>
            <div className="nav-brand-sub">{t('setup.previewOwner')}</div>
          </div>
        </div>
      </aside>
    </div>
  );
}

function DetailsStep({ draft, setDraft }: StepProps) {
  const t = useT();
  return (
    <div className="col">
      <h2>{t('setup.detailsH')}</h2>
      <p className="muted">{t('setup.detailsP')}</p>
      <div className="grid c2">
        <Field label={t('settings.address')}>
          <input value={draft.address} onChange={(e) => setDraft({ ...draft, address: e.target.value })} autoFocus />
        </Field>
        <Field label={t('common.phone')}>
          <input value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} dir="ltr" inputMode="tel" />
        </Field>
        <Field label={t('settings.taxNumber')}>
          <input value={draft.taxNumber} onChange={(e) => setDraft({ ...draft, taxNumber: e.target.value })} className="tnum" dir="ltr" />
        </Field>
        <Field label={t('settings.vatRate')} hint={t('setup.vatHint')}>
          <input
            type="number"
            step="0.5"
            min={0}
            max={100}
            value={Number.isFinite(draft.vatRate) ? draft.vatRate : ''}
            onChange={(e) => setDraft({ ...draft, vatRate: e.target.value === '' ? NaN : Number(e.target.value) })}
            className="tnum"
          />
        </Field>
      </div>
    </div>
  );
}

function ReceiptStep({ draft, setDraft, cashier }: StepProps & { cashier: string }) {
  const t = useT();
  const doc = useMemo(() => {
    const vat = Number.isFinite(draft.vatRate) ? draft.vatRate : 0;
    const payable = 43_800;
    const vatAmount = Math.round((payable * vat) / (100 + vat));
    const sample: ReceiptData = {
      receiptNo: 'T1-000001',
      createdAt: new Date().toISOString(),
      cashierName: cashier,
      lines: [
        { nameEn: 'Linen shirt', nameAr: 'قميص كتان', size: 'M', colorEn: 'Sand', colorAr: 'رملي', qty: 1, unitPrice: 14_900, net: 14_900 },
        { nameEn: 'Denim jacket', nameAr: 'جاكيت جينز', size: 'L', colorEn: 'Indigo', colorAr: 'نيلي', qty: 1, unitPrice: 28_900, net: 28_900 },
      ],
      gross: payable,
      discountTotal: 0,
      total: payable,
      rounding: 0,
      payable,
      vatAmount,
      netAmount: payable - vatAmount,
      vatRate: vat,
      changeIls: 1_200,
      payments: [{ method: 'cash', currency: 'ILS', amount: 45_000, amountIls: 45_000 }],
    };
    return printPage(receiptHtml(sample, draft), { widthMm: 80, title: 'Preview' });
  }, [draft, cashier]);

  return (
    <div className="setup-split">
      <div className="col">
        <h2>{t('setup.receiptH')}</h2>
        <p className="muted">{t('setup.receiptP')}</p>
        <Field label={t('settings.receiptLang')}>
          <div className="btn-row">
            {(['ar', 'en', 'both'] as const).map((v) => (
              <Button
                key={v}
                type="button"
                variant={draft.receiptLang === v ? 'primary' : 'default'}
                onClick={() => setDraft({ ...draft, receiptLang: v })}
              >
                {v === 'both' ? t('settings.receiptBoth') : v === 'ar' ? 'العربية' : 'English'}
              </Button>
            ))}
          </div>
        </Field>
        {draft.receiptLang !== 'en' ? (
          <Field label={`${t('settings.receiptFooter')} (العربية)`}>
            <textarea value={draft.receiptFooterAr} onChange={(e) => setDraft({ ...draft, receiptFooterAr: e.target.value })} dir="rtl" rows={2} />
          </Field>
        ) : null}
        {draft.receiptLang !== 'ar' ? (
          <Field label={`${t('settings.receiptFooter')} (English)`}>
            <textarea value={draft.receiptFooterEn} onChange={(e) => setDraft({ ...draft, receiptFooterEn: e.target.value })} dir="ltr" rows={2} />
          </Field>
        ) : null}
      </div>
      <aside className="setup-preview">
        <div className="xsmall muted">{t('setup.previewReceipt')}</div>
        <iframe className="receipt-frame" title={t('setup.previewReceipt')} srcDoc={doc} tabIndex={-1} />
      </aside>
    </div>
  );
}

function LookStep({ draft, setDraft }: StepProps) {
  const t = useT();
  const { lang } = useI18n();
  const mode = resolveMode(draft.theme);
  return (
    <div className="col">
      <h2>{t('setup.lookH')}</h2>
      <p className="muted">{t('setup.lookP')}</p>
      <Field label={t('settings.accent')}>
        <div className="swatches">
          {ACCENT_PRESETS.map((p) => {
            const on = p.hex.toLowerCase() === (draft.accent || DEFAULT_ACCENT).toLowerCase();
            return (
              <button
                key={p.key}
                type="button"
                className={`swatch ${on ? 'on' : ''}`}
                title={lang === 'ar' ? p.ar : p.en}
                aria-label={lang === 'ar' ? p.ar : p.en}
                aria-pressed={on}
                style={{ background: accentShades(p.hex, mode).accent }}
                onClick={() => setDraft({ ...draft, accent: p.hex })}
              />
            );
          })}
        </div>
      </Field>
      <Field label={t('settings.theme')}>
        <div className="btn-row">
          {(['auto', 'light', 'dark'] as const).map((k) => (
            <Button key={k} type="button" variant={draft.theme === k ? 'primary' : 'default'} onClick={() => setDraft({ ...draft, theme: k })}>
              {k === 'auto' ? t('settings.themeAuto') : k === 'light' ? t('settings.themeLight') : t('settings.themeDark')}
            </Button>
          ))}
        </div>
      </Field>
    </div>
  );
}

function PasswordStep({ onDone }: { onDone: () => void }) {
  const t = useT();
  const toast = useToast();
  const { user, device } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const mismatch = again.length > 0 && next !== again;

  const change = async () => {
    setBusy(true);
    setError('');
    try {
      let remember = false;
      try {
        remember = localStorage.getItem('pos.rememberMe') === '1';
      } catch {
        /* keep the default */
      }
      const r = await api<{ token: string }>('/auth/password', { body: { current, next, remember } });
      // The change ended every older session, this one included: keep the new one.
      setToken(r.token);
      if (user) {
        await rememberForOffline(user.username, next, { token: r.token, user, device: device ?? null }).catch(() => undefined);
      }
      toast.ok(t('setup.pwDone'));
      onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="col" style={{ maxWidth: 440 }}>
      <h2>{t('setup.pwH')}</h2>
      <p className="muted">{t('setup.pwP')}</p>
      <Field label={t('pf.currentPassword')}>
        <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" dir="ltr" autoFocus />
      </Field>
      <Field label={t('pf.newPassword')} hint={t('setup.pwMin')}>
        <input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" dir="ltr" />
      </Field>
      <Field label={t('pf.confirmPassword')} error={mismatch ? t('pf.mismatch') : undefined}>
        <input type="password" value={again} onChange={(e) => setAgain(e.target.value)} autoComplete="new-password" dir="ltr" />
      </Field>
      {error ? (
        <p role="alert" style={{ color: 'var(--danger)', margin: 0 }}>
          {error}
        </p>
      ) : null}
      <div>
        <Button variant="primary" busy={busy} disabled={!current || next.length < 8 || next !== again} onClick={() => void change()}>
          {t('setup.pwChange')}
        </Button>
      </div>
    </div>
  );
}

function DoneStep({ name, busy, onFinish }: { name: string; busy: boolean; onFinish: (path: string) => void }) {
  const t = useT();
  const { can } = useAuth();
  return (
    <div className="setup-done">
      <div className="big" aria-hidden>✓</div>
      <h2>{t('setup.doneH', { name })}</h2>
      <p className="muted">{t('setup.doneP')}</p>
      <div className="btn-row" style={{ justifyContent: 'center' }}>
        {can('catalog.edit') ? (
          <Button busy={busy} onClick={() => onFinish('/products')}>
            {t('setup.doneProducts')}
          </Button>
        ) : null}
        {can('users.edit') ? (
          <Button busy={busy} onClick={() => onFinish('/users')}>
            {t('setup.doneStaff')}
          </Button>
        ) : null}
        <Button variant="primary" size="lg" busy={busy} onClick={() => onFinish('/checkout')}>
          {t('setup.doneSell')}
        </Button>
      </div>
    </div>
  );
}
